import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { service } from '@ember/service';
import { Request } from '@warp-drive/ember';
import { programTemplateSchoolsQuery } from 'tio-data/requests/lpn-page';
import { discountLetterSchoolsQuery } from 'tio-data/requests/lpn-discount-letter';
import type { TasProgramTemplateInstitutionExtension as TasProgramTemplateInstitution } from 'tio-data/data/resources/tas-program-template-institution.ext';
import type { LpnDiscountLetterSchool } from 'tio-data/data/resources/lpn-discount-letter-school.type';
import type PartnerService from 'tio-employee/services/partner';
import type SessionContextService from 'tio-employee/services/session-context';

/** A program as the gate needs it: its id and whether it only allows its curated school list. */
export interface LpnGateTemplate {
  id: string | null | undefined;
  institutionListIsExclusive?: boolean;
}

/**
 * The LPN identifiers (`dapip_<id>` / `tas_<id>`) the employee's curated programs allow that the
 * company also offers through LPN.
 */
export function curatedLpnIdentifiers(
  links: readonly TasProgramTemplateInstitution[],
  offered: readonly LpnDiscountLetterSchool[]
): string[] {
  const offeredIds = new Set(offered.map((school) => school.institutionIdentifier));
  const curated = links.flatMap((link) => {
    const ids: string[] = [];
    if (link.accreditedInstitution?.id) ids.push(`dapip_${link.accreditedInstitution.id}`);
    if (link.unaccreditedInstitution?.id) ids.push(`tas_${link.unaccreditedInstitution.id}`);
    return ids;
  });
  return [...new Set(curated)].filter((id) => offeredIds.has(id));
}

interface LpnSchoolsGateSignature {
  Args: {
    /** The programs whose school rules decide whether, and which, LPN schools apply. */
    templates: readonly LpnGateTemplate[];
    /** Counts only accredited schools on curated lists, for programs that reject unaccredited ones. */
    accreditedOnly?: boolean;
  };
  Blocks: {
    /**
     * Rendered only when LPN is on and a program can use an LPN school. `identifiers` is null when
     * a program allows any school, otherwise the curated LPN schools the company offers.
     */
    default: [identifiers: readonly string[] | null, companySettingId: string];
  };
}

/**
 * Renders its block only when the employee can use an LPN school with one of the given programs:
 * one that allows every school, or a curated list that includes a school the company offers. A
 * failed curated-list check renders nothing rather than breaking the page.
 */
export default class LpnSchoolsGate extends Component<LpnSchoolsGateSignature> {
  @service declare partner: PartnerService;
  @service declare sessionContext: SessionContextService;

  get companySettingId() {
    return this.partner.companySettings?.id ?? null;
  }

  get employeeId() {
    return this.sessionContext.currentEmployee?.id ?? null;
  }

  get templateIds() {
    return this.args.templates.map((template) => template.id).filter((id): id is string => !!id);
  }

  get isLpnOn() {
    const lpn = this.partner.companySettings?.lpn;
    return Boolean(lpn && 'isEnabled' in lpn && lpn.isEnabled);
  }

  get isAvailable() {
    return this.isLpnOn && Boolean(this.employeeId) && this.templateIds.length > 0;
  }

  get allowsAnySchool() {
    return this.args.templates.some((template) => !template.institutionListIsExclusive);
  }

  /** The curated LPN schools the employee can actually pick, honouring `accreditedOnly`. */
  curatedIdentifiers = (
    links: readonly TasProgramTemplateInstitution[],
    offered: readonly LpnDiscountLetterSchool[]
  ) => {
    const identifiers = curatedLpnIdentifiers(links, offered);
    return this.args.accreditedOnly
      ? identifiers.filter((id) => !id.startsWith('tas_'))
      : identifiers;
  };

  @cached
  get curatedSchoolsQuery() {
    return programTemplateSchoolsQuery(this.templateIds);
  }

  @cached
  get offeredSchoolsQuery() {
    return this.employeeId ? discountLetterSchoolsQuery(this.employeeId) : null;
  }

  <template>
    {{#if this.isAvailable}}
      {{#if this.companySettingId}}
        {{#if this.allowsAnySchool}}
          {{yield null this.companySettingId}}
        {{else if this.offeredSchoolsQuery}}
          <Request @query={{this.curatedSchoolsQuery}}>
            <:error></:error>
            <:content as |curated|>
              <Request @query={{this.offeredSchoolsQuery}}>
                <:error></:error>
                <:content as |offered|>
                  {{#let (this.curatedIdentifiers curated.data offered.data) as |identifiers|}}
                    {{#if identifiers.length}}
                      {{yield identifiers this.companySettingId}}
                    {{/if}}
                  {{/let}}
                </:content>
              </Request>
            </:content>
          </Request>
        {{/if}}
      {{/if}}
    {{/if}}
  </template>
}
